import { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  CircularProgress,
  Typography,
  useTheme
} from "@mui/material";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import {  useSearchProjects } from "../../features/projects/mutations";

import { useDebounce } from "../../hooks/useDebounce";
import { useNavigation } from "../../hooks/useNavigation";

interface Project {
  id: number;
  title: string;
  project_reference: string;
  address: string;
}

interface ProjectSwitcherProps {
  currentProjectId: string;
}

export const ProjectSwitcher = ({ currentProjectId }: ProjectSwitcherProps) => {

  const theme = useTheme();

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  const debouncedSearch = useDebounce(searchTerm, 300);
  const searchProjects = useSearchProjects();

  useEffect(() => {
    const fetchCurrentProject = async () => {
      try {
        const params: Record<string, any> = {
          project_id: currentProjectId
        };
        const response = await searchProjects.mutateAsync({ params });
        const rows = response?.data ?? [];
        const foundProject = rows.find(
          (p: Project) => p.id === Number(currentProjectId)
        );
        if (foundProject) {
          setCurrentProject(foundProject);
        } else {
          setCurrentProject(null);
        }
      } catch (error) {
        console.error("Error fetching current project:", error);
      }
    };
    if (currentProjectId) {
      fetchCurrentProject();
    }
  }, [currentProjectId]);

  useEffect(() => {
    if (open) {
      const fetchData = async () => {
        try {
          const params: Record<string, any> = {
            search_term: debouncedSearch || "",
          };
          const response = await searchProjects.mutateAsync({ params });
          const rows = response?.data ?? [];
          setProjects(rows);
        } catch (error) {
          console.error("Error fetching projects:", error);
          setProjects([]);
        }
      };
      fetchData();
    }
  }, [debouncedSearch, open]);

  const { goToProjectDetails } = useNavigation();

  const handleProjectChange = (_event: unknown, newValue: Project | null) => {
    if (newValue) {
      setCurrentProject(newValue);
      goToProjectDetails(newValue.id, newValue.title);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm("");
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <LocationOnIcon sx={{ color: 'text.secondary' }} fontSize="small" />
      <Autocomplete
        sx={{
          minWidth: 300,
          maxWidth: 400,
          "& .MuiOutlinedInput-root": {
            "& fieldset": { border: "none" },
            "&:hover fieldset": { border: "none" },
            "&.Mui-focused fieldset": { border: "none" },
            backgroundColor: "transparent",
            color: "text.secondary",
            ...theme.typography.subtitle2
          },
        }}
        size="small"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        value={currentProject}
        onChange={handleProjectChange}
        inputValue={open ? searchTerm : currentProject?.title || ""}
        onInputChange={(_event, newInputValue, reason) => {
          if (reason === "input") {
            setSearchTerm(newInputValue);
          }
        }}
        options={projects}
        loading={searchProjects.isPending}
        popupIcon={null}
        getOptionLabel={(option) => option.title || ""}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              currentProject?.title ? currentProject?.title : "Switch project..."
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searchProjects.isPending && (
                    <CircularProgress color="inherit" size={20} />
                  )}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box>
              <Typography variant="subtitle2" fontWeight={500}>
                {option.title}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.project_reference}
                {option.address && ` • ${option.address}`}
              </Typography>
            </Box>
          </Box>
        )}
        noOptionsText={
          searchTerm ? "No projects found" : "Type to search projects..."
        }
      />
      <UnfoldMoreIcon sx={{ color: 'text.secondary' }} fontSize="small" />
    </Box>
  );
};